'use client';

import { motion } from 'motion/react';
import SectionHeader from '@/components/shared/section-header';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import KeywordRow from '@/components/sections/keyword-marquee/row';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { useIsMobile } from '@/hooks/use-mobile';
import type { KeywordMarqueeSectionContent } from '@/sanity/lib/sections';

export default function KeywordMarquee({
  content,
}: {
  content?: KeywordMarqueeSectionContent | null;
}) {
  const isMobile = useIsMobile();
  const { sectionSequenceBlockVariants, sectionSequenceRevealVariants } =
    useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();

  if (!content || content.show === false) return null;

  const rowOne = content?.marquee?.rowOne ?? [];
  const rowTwo = content?.marquee?.showRowTwo === false ? [] : (content?.marquee?.rowTwo ?? []);
  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);
  const showMarqueeBlock = content.showMarquee !== false && rowOne.length > 0;
  const { header } = content;
  const hasHeaderBody = Boolean(
    content.showHeader !== false &&
    (header?.tagline || header?.heading?.length || header?.subheading),
  );

  if (!showMarqueeBlock && !hasHeaderBody && !primary) return null;

  const renderMarqueeItem = (item: string) => (
    <span key={item} className="inline-flex items-center gap-3 pr-3">
      <span>{item}</span>
      <span aria-hidden="true" className="text-[#FCFAD7]">
        &nbsp;&nbsp;·&nbsp;&nbsp;
      </span>
    </span>
  );

  const hasContainedContent = hasHeaderBody || Boolean(primary);

  return (
    <section className="relative left-1/2 w-dvw max-w-dvw -translate-x-1/2 overflow-hidden bg-neutral-900 dark:bg-black dark:border-y py-6 sm:py-12">
      <motion.div
        className="space-y-6 sm:space-y-8"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        {hasContainedContent ? (
          <div className="mx-auto max-w-7xl space-y-6 sm:space-y-8 px-4 sm:px-6 min-[1324px]:px-0">
            {hasHeaderBody ? (
              <SectionHeader
                sequenceChild
                className=""
                content={{
                  eyebrow: header?.tagline,
                  description: header?.subheading,
                }}
                titleSlot={
                  header?.heading?.length ? (
                    <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-white">
                      <SanityInlineText
                        blocks={header.heading}
                        emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
                      />
                    </h2>
                  ) : undefined
                }
                eyebrowClassName="text-[#FCFAD7]/80 dark:text-[#FCFAD7]/80"
                emphasisClassName="text-[#FCFAD7] dark:text-[#FCFAD7]"
                headingClassName="text-white"
                subheadingClassName="text-muted-foreground"
              />
            ) : null}
            {primary ? (
              <motion.div variants={sectionSequenceBlockVariants}>
                <SectionCtaPair
                  primary={primary}
                  secondary={secondary}
                  trackingLocation="keyword_marquee"
                  className="mx-auto max-w-7xl max-xl:px-0"
                />
              </motion.div>
            ) : null}
          </div>
        ) : null}
        {showMarqueeBlock ? (
          <div className="w-full space-y-6">
            <motion.div variants={sectionSequenceBlockVariants}>
              <KeywordRow
                baseVelocity={isMobile ? 0.2 : 0.5}
                maxScrollFactor={0}
                className="pb-1 text-white text-xl sm:text-4xl leading-none font-extralight italic"
              >
                {rowOne.map(renderMarqueeItem)}
              </KeywordRow>
            </motion.div>
            {rowTwo.length ? (
              <motion.div variants={sectionSequenceBlockVariants}>
                <KeywordRow
                  baseVelocity={isMobile ? -0.2 : -0.5}
                  maxScrollFactor={0}
                  className="text-muted-foreground text-lg sm:text-2xl leading-none font-extralight italic"
                >
                  {rowTwo.map(renderMarqueeItem)}
                </KeywordRow>
              </motion.div>
            ) : null}
          </div>
        ) : null}
      </motion.div>
    </section>
  );
}
